/**
 * This file should be shared between the webshop-backend and the webshop-frontend.
 * Whenever a modification is made to this file, make sure to copy its content and paste it in:
 *  - webshop-backend/src/routes/cms/shared_types.ts
 *  - webshop-frontend/src/types/cms_shared_types.ts
 */

// COMMON
export type Language = 'sv' | 'en';

export type MeasurePeriod = 'month' | 'year';

export interface Content {
  name: string;
}

export interface ResourceText {
  key: string;
  value: string;
}

export interface RichText {
  type: string;
  content: {
    type: string;
    content: {
      type: string;
      text: string;
      marks?: {
        type: string;
      }[];
    }[];
  }[];
}

// BREV-FRAN-OSS CONTENT

export interface BrevFranOssContent extends Content {
  header: string;
  title: string;
  description: RichText;
  yesAnswer: string;
  noAnswer: string;
  firstSectionTitle: string;
  firstSectionDescription: string;
  firstSectionSuccessMessage: string;
  createContractInformation: string;
  createContractText: string;
  createContractLink: string;
  submissionFormMessage: RichText;
  submitButton: string;
  submitOnNoMessage: string;
  submitSuccessfulMessage: string;
  submitErrorMessage: string;
  datePrompt: string;
  inputRequiredError: string;
  invalidReferenceNumberError: string;
  invalidEmailError: string;
  invalidPhoneNumberError: string;
  questions: Question[];
  userInputs: UserInput[];
  landingPageYes: BrevFranOssLandingPage;
  landingPageNo: BrevFranOssLandingPage;
  leavePageConfirmation: LeavePageConfirmation;
}

export interface BrevFranOssLandingPage {
  title: string;
  text: string;
  buttonText: string;
}

export interface Question {
  key: string;
  question: string;
  explanation: string;
}

export interface UserInput {
  key: string;
  prompt: string;
  placeholder: string;
}

export interface UserSelectInput {
  key: string;
  prompt: string;
  placeholder: string;
  options: UserSelectOption[];
}

export interface UserSelectOption {
  key: string;
  displayValue: string;
  backendValue?: string;
}

// SHOP CONTENT

export interface ShopContent extends Content {
  breadcrumb: Breadcrumb;
  confirmationDialog: ConfirmationDialog;
  shoppingCart: ShoppingCart;
  productFamilyStep: ProductFamilyStep;
  productChoiceStep: ProductChoiceStep;
  summaryStep: SummaryStep;
  addonsStep: AddonsStep;
  informationStep: InformationStep;
  invoiceStep: InvoiceStep;
  contactStep: ContactStep;
  signingPageStep: SigningPageStep;
  landingPageStep: LandingPageStep;
  continueButton: string;
  priceWithMonthFormat: string;
  priceWithoutMonthFormat: string;
  inputRequiredError: string;
  invalidEmailError: string;
  invalidPhoneNumberError: string;
  invalidOrganisationNumberError: string;
  invalidLuhnOrganisationNumberError: string;
  invalidPostCodeError: string;
  invalidCityError: string;
  invalidCharacterError: string;
  invalidDateRangeError: string;
  helpButton: string;
  reportingProductSummary: string;
  categories: Category[];
  addonCategories: AddonCategory[];
  leavePageConfirmation: LeavePageConfirmation;
}

interface LeavePageConfirmation {
  title: string;
  description: RichText;
}

interface Breadcrumb {
  backButton: string;
  steps: ResourceText[];
}

interface ConfirmationDialog {
  titleWhenSwitchingVerksamhetstyp: string;
  descriptionWhenSwitchingVerksamhetstyp: RichText;
  titleWhenSwitchingSubcategory: string;
  descriptionWhenSwitchingSubcategory: RichText;
  noButton: string;
  yesButton: string;
}

interface ShoppingCart {
  title: string;
  summary: string;
  calculationButton: string;
  fromSami: string;
  resetProcessConfirmationTitle: string;
  resetProcessConfirmationDescription: RichText;
  resetProcessConfirmationAddonsDescription: RichText;
  addons: string;
  shoppingCartModal: ShoppingCartModal;
  snackbar: ShoppingCartSnackbar;
}

interface ShoppingCartModal {
  title: string;
  total: string;
  vat: string;
  closeButton: string;
  reporting: string;
  reportingTooltip: string;
  simulatePrice: string;
}

interface ShoppingCartSnackbar {
  added: string;
  removed: string;
  updated: string;
  cartEmptied: string;
}

interface ProductFamilyStep {
  title: string;
  tipTitle: string;
  tipContent: RichText;
  footer: Footer;
}

interface ProductChoiceStep {
  title: string;
  tipTitleBefore: string;
  tipContentBefore: RichText;
  tipTitleAfter: string;
  tipContentAfter: RichText;
  addProductButton: string;
  editProductButton: string;
  removeProductButton: string;
  findExtraProductsButton: string;
  categoryHeader: string;
  subcategoryTooltip: string;
  loseProgressWarning: string;
  extraProductsTooltip: string;
  extraProductClosed: string;
  extraProductsClosed: string;
  extraProductOpened: string;
  extraProductsOpened: string;
  reportingProductInformation: string;
  selectionHeader: string;
  productConfigurationModal: ProductConfigurationModal;
  footer: Footer;
  pickSubcategorySelectText: string;
  pickSubcategoryDescriptionText: string;
  removeAddonsTitle: string;
  removeAddonsDescription: RichText;
}

interface ProductConfigurationModal {
  saveButton: string;
  radioTvTitle: string;
  radioTvDescription: string;
  dayCountTitle: string;
  dayCountDescription: string;
  inputErrorMessage: string;
  inputTooLowErrorMessage: string;
  inputTooHighErrorMessage: string;
  dayCountText: RichText;
  seasonallyOpen: string;
  seasonallyOpenDescription: string;
}

interface SummaryStep {
  title: string;
  subtitle: string;
  informationTitle: string;
  informationTooltip: string;
  informationPlaceholder: string;
  footer: Footer;
}

interface AddonsStep {
  title: string;
  tipTitle: string;
  tipContent: RichText;
  addAddonButton: string;
  readMoreButton: string;
  switchToAddonButton: string;
  editAddonButton: string;
  removeAddonButton: string;
  confirmButton: string;
  categoryHeader: string;
  addonCategoryTooltip: string;
  selectionHeader: string;
  footer: Footer;
  pickSubcategorySelectText: string;
  pickSubcategoryDescriptionText: string;
  updatedPricing: string;
  includedProducts: string;
}

interface InformationStep {
  title: string;
  subtitle: string;
  tipTitleBefore: string;
  tipContentBefore: RichText;
  tipTitleAfter: string;
  tipContentAfter: RichText;
  userInputs: UserInput[];
  userSelectInputs: UserSelectInput[];
  footer: Footer;
}

export interface InvoiceStep {
  title: string;
  subtitle: string;
  invoiceAddressTitle: string;
  invoiceAddressDescription: string;
  startDatePrompt: string;
  invoicePeriodTitle: string;
  invoicePeriods: ResourceText[];
  monthlyInvoiceThreshold: number;
  monthlyInvoiceExtraName: string;
  monthlyInvoiceExtraDescription: string;
  monthlyInvoiceExtraPrice: number;
  defaultInvoicePeriodForReporting: string;
  defaultInvoicePeriodReviewWarning: string;
  forceMonthlyInvoicing: string;
  invoiceMethodTitle: string;
  invoiceMethods: ResourceText[];
  paperInvoiceExtraName: string;
  paperInvoiceExtraDescription: string;
  paperInvoiceExtraPrice: number;
  userInputs: UserInput[];
  userSelectInputs: UserSelectInput[];
  footer: Footer;
  emailInvoice: EmailInvoice;
  ediInvoice: EdiInvoice;
  paperInvoice: PaperInvoice;
}

interface EmailInvoice {
  key: string;
  title: string;
}

interface EdiInvoice {
  gln: string;
  key: string;
  title: string;
  formats: ResourceText[];
  operatorTitle: string;
  component: string;
  reference: string;
  formatTitle: string;
  chooseFormat: string;
  chooseOperator: string;
  glnPlaceholder: string;
  invalidGlnFormat: string;
  peppolIdTitle: string;
  peppolIdPlaceholder: string;
  invalidPeppolIdFormat: string;
  ediOperators: EdiOperator[];
}

export interface EdiOperator {
  name: string;
  key: string;
}

interface PaperInvoice {
  key: string;
  title: string;
}

export interface AddonConditionsDescription {
  description: RichText;
  addonProvider: AddonProvider;
}

export interface ContactStep {
  title: string;
  subtitle: string;
  accountingContactTitle: string;
  accountingContactDescription: string;
  agreementConditionsTitle: string;
  agreementConditionsDescription: RichText;
  addonConditionsDescriptions: AddonConditionsDescription[];
  signButton: string;
  userInputs: UserInput[];
  footer: Footer;
  soundtrackProvider: AddonProvider;
  soundtrackAccountTitle: string;
  soundtrackAccountDescription: string;
}

export interface SigningPageStep {
  title: string;
  subtitle: string;
  description: string;
  redirectButton: string;
  cancelButton: string;
}

export interface LandingPageStep {
  title: string;
  descriptionTitle: string;
  soundtrackDescriptionTitle: string;
  descriptionText: RichText;
  bottomText: RichText;
  buttonText: string;
  buttonLinkUrl: string;
  errorTitle: string;
  errorDescriptionTitle: string;
  errorDescriptionText: string;
  errorButtonText: string;
}

export interface Category {
  key: string;
  name: string;
  subtitle?: string;
  explanation: RichText;
  iconClickedFilename: string;
  iconNotClickedFilename: string;
  subcategories: Subcategory[];
  helpTextTitle?: string;
  helpTextDescription?: RichText;
  enableAddons: boolean;
}

export interface CmsVirtualProduct {
  name: string;
  order: number;
  description: string;
  templateProduct: CmsProduct;
  products: CmsProduct[];
}

export interface Subcategory {
  key: string;
  name: string;
  applicationArea: string;
  defaultInvoicePeriod: string;
  products: CmsProduct[];
  extraProducts: CmsProduct[];
  virtualProducts: CmsVirtualProduct[];
  helpTextTitle?: string;
  helpTextDescription?: RichText;
  additionalConditionsDescription?: RichText;
}

export interface AddonCategory {
  key: string;
  name: string;
  addons: Addon[];
  disallowMultipleAddons: boolean;
  helpTextTitle?: string;
  helpTextDescription?: RichText;
}

export interface Footer {
  displayFooter: boolean;
  mediaUrl: string;
  mediaFooter: RichText;
  leftColumn: RichText;
  rightColumn: RichText;
}

export enum BREADCRUMB_KEY {
  PRODUCT_FAMILY_STEP = 'product_family_step',
  PRODUCT_CHOICE_STEP = 'product_choice_step',
  SUMMARY_STEP = 'summary_step',
  ADDONS_STEP = 'addons_step',
  INFORMATION_STEP = 'information_step',
  INVOICE_STEP = 'invoice_step',
  CONTACT_STEP = 'contact_step',
}

// PRODUCT

export interface CmsProductBase {
  // General
  code: string;
  name?: string;
  productGroupIdentifier?: string;
  description: string;
  measurePrompt: string;
  measurePlaceholder: string;
  measureUnit: string;
  measureInputType: string;
  measureOptions: ProductInputTypeOption[];
  measureOptionsWithCustomLastOption: boolean;
  measureOptionsLastOptionName: string;
  measureOptionsPlaceholder: string;
  measureOptionsMin: number;
  measureOptionsMax: number;
  timeMeasurePrompt: string;
  timeMeasurePlaceholder: string;
  timeMeasureUnit: string;
  timeMeasureInputType: string;
  timeMeasureOptions: ProductInputTypeOption[];
  timeMeasureOptionsWithCustomLastOption: boolean;
  timeMeasureOptionsLastOptionName: string;
  timeMeasureOptionsPlaceholder: string;
  timeMeasureOptionsMin: number;
  timeMeasureOptionsMax: number;
  seasonSelection: SeasonSelectionOption;
  startDatePrompt?: string;
  endDatePrompt?: string;
  periods?: string;

  // Simulation
  measureDisableSimulation: boolean;
  measureMin?: string | number;
  measureMax?: string | number;
  measurePeriod?: MeasurePeriod;
  timeMeasureDisableSimulation: boolean;
  timeMeasureMin?: string | number;
  timeMeasureMax?: string | number;

  // Schablon
  measureSchablonMin?: string | number;
  measureSchablonMax?: string | number;
  measureSchablonUnit: string;
  timeMeasureSchablonMin?: string | number;
  timeMeasureSchablonMax?: string | number;
  timeMeasureSchablonUnit: string;
}

export interface CmsProduct extends CmsProductBase {
  // General
  name: string;
  order: number;
  reporting: boolean;
  musicClass: string;
  hasMeasure: boolean;
  hasTimeMeasure: boolean;

  // Simulation
  measureMin?: number;
  measureMax?: number;
  measurePeriod?: MeasurePeriod;
  timeMeasureMin?: number;
  timeMeasureMax?: number;

  // Schablon
  isSchablon: boolean;
  measureSchablonMin?: number;
  measureSchablonMax?: number;
  timeMeasureSchablonMin?: number;
  timeMeasureSchablonMax?: number;

  // Discount
  triggerDiscount?: string[];
  discountReplacementProduct?: string;
}

export interface ProductInputTypeOption {
  displayValue: string;
  salesforceValue: number;
}

export enum SeasonSelectionOption {
  DISABLED = 'disabled',
  OPTIONAL = 'optional',
  MANDATORY = 'mandatory',
}

// ADDON
type AddonProviderIdentifier = 'SOUNDTRACK' | 'UNKNOWN';

type SoundtrackSubscriptionPlan = 'ESSENTIAL' | 'UNLIMITED';

interface SoundtrackRegistrationData {
  plan: SoundtrackSubscriptionPlan;
}

// Can be extended with the | operator for other providers.
type ProviderRequiredData = SoundtrackRegistrationData;

export interface AddonProvider {
  identifier: AddonProviderIdentifier;
  uuid: string;
  name: string;
  description?: string;
  imageUrl?: string;
  forceMonthlyInvoicing: boolean;
}

export interface Addon {
  code: string;
  name: string;
  description?: string;
  addonDetails: AddonDetails;
  addonProvider: AddonProvider;
  measurePeriod: MeasurePeriod;
  monthlyPriceExclVat?: number;
  monthlyPriceInclVat?: number;
  providerRequiredData: ProviderRequiredData;
}

export interface AddonDetails {
  title: string;
  preamble: string;
  imageUrl?: string;
  shortName?: string;
  description?: RichText;
  offer?: RichText;
}
